import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  useMediaQuery,
} from "@mui/material";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import CustomerSearchAutocomplete from "components/CustomerSearchAutocomplete";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { TEXT_DARK, TEXT_SECONDARY } from "components/bento/bentoTokens";
import {
  Refresh as RefreshIcon,
  Replay as RetryIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Bolt as BoltIcon,
  Delete as DeleteIcon,
  PauseCircle as PauseIcon,
  RestoreFromTrash as RestoreIcon,
  Send as ResendIcon,
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import moment from "moment";
import Swal from "sweetalert2";
import io from "socket.io-client";
import {
  listQueueJobs,
  retryQueueJob,
  restoreQueueJob,
  cancelQueueJob,
  deleteQueueJob,
  bulkRetryJobs,
  bulkCancelJobs,
  bulkDeleteJobs,
} from "api/whatsapp";
import { getAllCustomerGroups } from "api/customerGroup";

const STATUS_COLORS = {
  pending: "warning",
  scheduled: "info",
  processing: "info",
  retry_scheduled: "warning",
  sent: "success",
  partially_sent: "warning",
  failed: "error",
  cancelled: "default",
};

const MESSAGE_TYPE_LABELS = {
  arrival_notification: "Notify",
  delivery_notification: "Delivery",
  agent_notification: "Agent",
  manual_message: "Manual",
  bulk_message: "Bulk",
  system_message: "System",
};

const ALL_STATUSES = [
  "pending",
  "scheduled",
  "processing",
  "retry_scheduled",
  "sent",
  "partially_sent",
  "failed",
  "cancelled",
];

const ALL_TYPES = [
  "arrival_notification",
  "delivery_notification",
  "agent_notification",
  "manual_message",
  "bulk_message",
  "system_message",
];

const WhatsAppQueue = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const socketRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // searchInput = live-typed text; search = committed value actually sent to the
  // backend (debounced, or committed immediately via Enter/search icon on desktop).
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterCustomer, setFilterCustomer] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [limit, setLimit] = useState(20);
  const [selectionModel, setSelectionModel] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [groups, setGroups] = useState([]);

  // Human-readable group name for a WhatsApp group id (falls back to the raw id
  // if the group can't be resolved, e.g. it was deleted).
  const groupName = (groupId) => {
    if (!groupId) return "-";
    return groups.find((g) => g.id === groupId)?.name || groupId;
  };

  // Stable ref so socket callbacks always call the latest fetchJobs
  // without needing to re-register socket listeners on every filter change
  const fetchJobsRef = useRef(null);

  // Detail dialog
  const [detailJob, setDetailJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Desktop pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Mobile infinite scroll — server-side paging (like PackageList)
  const MOBILE_PAGE_SIZE = 20;
  const [mobilePage, setMobilePage] = useState(0);
  const [mobileHasMore, setMobileHasMore] = useState(true);

  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Plain function — NOT useCallback, same as PackageList's fetchPackages.
  // Re-created every render so it always reads latest state via closure.
  // Never used as a useEffect dep so it never triggers unwanted re-fetches.
  const fetchJobs = async (pageNum) => {
    setIsLoading(true);
    const skip = isMobile ? (pageNum ?? 0) * MOBILE_PAGE_SIZE : 0;
    const fetchLimit = isMobile ? MOBILE_PAGE_SIZE : limit;

    const res = await listQueueJobs({
      status: filterStatus || undefined,
      messageType: filterType || undefined,
      customerId: filterCustomer?._id || undefined,
      search: search || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      limit: fetchLimit,
      skip: skip || undefined,
      showDeleted,
    });

    if (res.success && Array.isArray(res.data)) {
      if (isMobile) {
        setRows((prev) => {
          const ids = new Set(prev.map((r) => r.id));
          const newItems = res.data.filter((r) => !ids.has(r.id));
          return [...prev, ...newItems];
        });
      } else {
        setRows(res.data);
      }
      setMobileHasMore(res.data.length === MOBILE_PAGE_SIZE);
    } else {
      Swal.fire({
        icon: "error",
        title: "Failed to load queue",
        text: res.message,
        timer: 4000,
        showConfirmButton: false,
        toast: true,
        position: "top-right",
      });
    }
    setIsLoading(false);
  };

  // Load customer groups once so job rows/dialog can show a name instead of the raw UUID.
  useEffect(() => {
    getAllCustomerGroups().then((res) => {
      if (res.success && Array.isArray(res.data)) setGroups(res.data);
    });
  }, []);

  // Socket.IO setup for real-time updates
  useEffect(() => {
    const socketUrl =
      process.env.NODE_ENV === "production"
        ? window.location.origin
        : process.env.REACT_APP_SOCKET_IO || "http://localhost:5000";

    socketRef.current = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current.on("connect", () => {
      console.log("✅ WhatsApp Queue Socket.IO connected");
      setIsConnected(true);
    });

    socketRef.current.on("disconnect", () => {
      console.log("❌ WhatsApp Queue Socket.IO disconnected");
      setIsConnected(false);
    });

    // Listen for queue job created/updated events
    socketRef.current.on("whatsapp-queue-job-created", (data) => {
      console.log("📨 New queue job created:", data);
      fetchJobsRef.current?.();
    });

    socketRef.current.on("whatsapp-queue-job-updated", (data) => {
      console.log("🔄 Queue job updated:", data);
      fetchJobsRef.current?.();
    });

    socketRef.current.on("whatsapp-queue-job-sent", (data) => {
      console.log("✅ Queue job sent:", data);
      fetchJobsRef.current?.();
    });

    socketRef.current.on("whatsapp-queue-job-failed", (data) => {
      console.log("❌ Queue job failed:", data);
      fetchJobsRef.current?.();
    });

    socketRef.current.on("whatsapp-queue-status-change", (data) => {
      console.log("📊 Queue status changed:", data);
      fetchJobsRef.current?.();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off("connect");
        socketRef.current.off("disconnect");
        socketRef.current.off("whatsapp-queue-job-created");
        socketRef.current.off("whatsapp-queue-job-updated");
        socketRef.current.off("whatsapp-queue-job-sent");
        socketRef.current.off("whatsapp-queue-job-failed");
        socketRef.current.off("whatsapp-queue-status-change");
        socketRef.current.disconnect();
      }
    };
  }, []); // stable — socket registered once; callbacks use fetchJobsRef

  const refreshAll = () => {
    setRows([]);
    setMobilePage(0);
    fetchJobs(0);
  };

  // Update ref synchronously every render so socket callbacks always get latest
  fetchJobsRef.current = refreshAll;

  // Fetch page when mobilePage changes
  useEffect(() => {
    fetchJobs(mobilePage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobilePage]);

  // When filters change: clear rows and reset to page 0
  useEffect(() => {
    setRows([]);
    setMobilePage(0);
    fetchJobs(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterType, filterCustomer, search, fromDate, toDate, limit, showDeleted]);

  // Debounce searchInput → search (which triggers the server fetch above).
  // Desktop's toolbar can also commit immediately via Enter/search icon.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Mobile infinite scroll — listens on the layout's main scroll container
  useEffect(() => {
    if (!isMobile) return;
    const container = document.getElementById("main-scroll-container") || window;

    const handleScroll = () => {
      const el = /** @type {Element} */ (container);
      const scrollTop    = container === window ? window.scrollY     : el.scrollTop;
      const clientHeight = container === window ? window.innerHeight : el.clientHeight;
      const scrollHeight = container === window ? document.documentElement.scrollHeight : el.scrollHeight;
      if (scrollTop + clientHeight >= scrollHeight - 200 && !isLoading && mobileHasMore) {
        setMobilePage((p) => p + 1);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isMobile, isLoading, mobileHasMore]);

  const handleRetry = async (job) => {
    const confirm = await Swal.fire({
      title: "Retry this job?",
      text: `Job ID: ${job.id}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Retry",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    const res = await retryQueueJob(job.id);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: "Job queued for retry",
        timer: 2000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      refreshAll();
    } else {
      Swal.fire({ icon: "error", title: "Retry failed", text: res.message });
    }
  };

  const handleResend = async (job) => {
    const confirm = await Swal.fire({
      title: "Resend this cancelled job?",
      text: `Job ID: ${job.id}\nThis will re-queue it for sending.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Resend",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    const res = await retryQueueJob(job.id);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: "Job re-queued for sending",
        timer: 2000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      refreshAll();
    } else {
      Swal.fire({ icon: "error", title: "Resend failed", text: res.message });
    }
  };

  const handleRestore = async (job) => {
    const confirm = await Swal.fire({
      title: "Restore this job?",
      text: `Job ID: ${job.id}\nThis will make the job visible and active again.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Restore",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2e7d32",
    });
    if (!confirm.isConfirmed) return;

    const res = await restoreQueueJob(job.id);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: "Job restored successfully",
        timer: 2000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      refreshAll();
    } else {
      Swal.fire({ icon: "error", title: "Restore failed", text: res.message });
    }
  };

  const handleCancel = async (job) => {
    const confirm = await Swal.fire({
      title: "Cancel this job?",
      text: `Job ID: ${job.id}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "No",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    const res = await cancelQueueJob(job.id);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: "Job cancelled",
        timer: 2000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      refreshAll();
    } else {
      Swal.fire({ icon: "error", title: "Cancel failed", text: res.message });
    }
  };

  const handleViewDetail = (job) => {
    setDetailJob(job);
    setDetailOpen(true);
  };

  const handleDelete = async (job) => {
    const confirm = await Swal.fire({
      title: "Delete this job?",
      text: `Job ID: ${job.id}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "No",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;
    const res = await deleteQueueJob(job.id);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: "Job deleted",
        timer: 2000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      refreshAll();
    } else {
      Swal.fire({ icon: "error", title: "Delete failed", text: res.message });
    }
  };

  const handleBulkRetry = async () => {
    const confirm = await Swal.fire({
      title: `Retry ${selectionModel.length} job(s)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Retry All",
    });
    if (!confirm.isConfirmed) return;
    const res = await bulkRetryJobs(selectionModel);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: `${res.data?.success ?? selectionModel.length} job(s) queued for retry`,
        timer: 2500,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      setSelectionModel([]);
      refreshAll();
    } else {
      Swal.fire({
        icon: "error",
        title: "Bulk retry failed",
        text: res.message,
      });
    }
  };

  const handleBulkCancel = async () => {
    const confirm = await Swal.fire({
      title: `Cancel ${selectionModel.length} job(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel All",
      confirmButtonColor: "#f57c00",
    });
    if (!confirm.isConfirmed) return;
    const res = await bulkCancelJobs(selectionModel);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: `${res.data?.success ?? selectionModel.length} job(s) cancelled`,
        timer: 2500,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      setSelectionModel([]);
      refreshAll();
    } else {
      Swal.fire({
        icon: "error",
        title: "Bulk cancel failed",
        text: res.message,
      });
    }
  };

  const handleBulkDelete = async () => {
    const confirm = await Swal.fire({
      title: `Delete ${selectionModel.length} job(s)?`,
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete All",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;
    const res = await bulkDeleteJobs(selectionModel);
    if (res.success) {
      Swal.fire({
        icon: "success",
        text: `${res.data?.success ?? selectionModel.length} job(s) deleted`,
        timer: 2500,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
      setSelectionModel([]);
      refreshAll();
    } else {
      Swal.fire({
        icon: "error",
        title: "Bulk delete failed",
        text: res.message,
      });
    }
  };

  const columns = [
    {
      field: "jobNumber",
      headerName: "#",
      width: 70,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {value ?? "-"}
        </Typography>
      ),
    },
    {
      field: "id",
      headerName: "Job ID",
      width: 220,
      hide: true,
      renderCell: ({ value }) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
        >
          {value}
        </Typography>
      ),
    },
    {
      field: "messageType",
      headerName: "Type",
      width: 160,
      renderCell: ({ value, row }) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            label={MESSAGE_TYPE_LABELS[value] || value}
            size="small"
            variant="outlined"
          />
          {row.totalParts > 1 && (
            <Chip
              label={`${row.partIndex}/${row.totalParts}`}
              size="small"
              color="info"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          color={STATUS_COLORS[value] || "default"}
        />
      ),
    },
    {
      field: "phoneNumber",
      headerName: "Phone",
      width: 150,
      valueGetter: (params) => params.row.destination?.phoneNumber || "-",
    },
    {
      field: "group",
      headerName: "Group",
      width: 160,
      valueGetter: (params) => groupName(params.row.provider?.selectedGroupId),
    },
    {
      field: "message",
      headerName: "Message",
      flex: 1,
      minWidth: 200,
      hide: true,
      valueGetter: (params) => params.row.content?.text || "-",
      renderCell: ({ value }) => (
        <Tooltip title={value}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "attempts",
      headerName: "Attempts",
      width: 90,
      valueGetter: (params) => {
        const a = params.row.attempts;
        return a ? `${a.current}/${a.max}` : "-";
      },
    },
    {
      field: "queuedAt",
      headerName: "Queued At",
      width: 160,
      valueGetter: (params) =>
        params.row.schedule?.queuedAt || params.row.createdAt,
      renderCell: ({ value }) =>
        value ? moment(value).format("DD/MM/YY HH:mm:ss") : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 190,
      sortable: false,
      renderCell: ({ row }) => {
        const isDeleted = row.audit?.validflag === false;
        return (
          <Box display="flex" gap={0.5}>
            <Tooltip title="View details">
              <IconButton size="small" onClick={() => handleViewDetail(row)}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isDeleted ? (
              <Tooltip title="Restore (set validflag back to true)">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleRestore(row)}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                {row.status === "failed" && (
                  <Tooltip title="Retry">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleRetry(row)}
                    >
                      <RetryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {row.status === "cancelled" && (
                  <Tooltip title="Resend (re-queue cancelled job)">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleResend(row)}
                    >
                      <ResendIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {["pending", "scheduled", "retry_scheduled"].includes(
                  row.status,
                ) && (
                  <Tooltip title="Cancel">
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => handleCancel(row)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(row)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box m="0 0rem">
      {/* Filters */}
      <Grid container spacing={2} mt={1} mb={2} alignItems="center">
        <Grid item xs={4} sm={4} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {ALL_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  <Chip
                    label={s}
                    size="small"
                    color={STATUS_COLORS[s] || "default"}
                    sx={{ mr: 1 }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Message Type</InputLabel>
            <Select
              value={filterType}
              label="Message Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {ALL_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {MESSAGE_TYPE_LABELS[t] || t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={2}>
          <CustomerSearchAutocomplete
            value={filterCustomer}
            onChange={setFilterCustomer}
            label="Customer"
            size="small"
            sx={{ width: "100%" }}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Last</InputLabel>
            <Select
              value={limit}
              label="Last"
              onChange={(e) => setLimit(e.target.value)}
            >
              {[20, 50, 100, 500, 1000].map((n) => (
                <MenuItem key={n} value={n}>
                  {n} jobs
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth
            size="small"
            label="From"
            type="datetime-local"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <TextField
            fullWidth
            size="small"
            label="To"
            type="datetime-local"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <Button
          fullWidth
            color="secondary"
            variant="outlined"
            startIcon={
              isLoading ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            onClick={refreshAll}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {/* Stats summary + Bulk action bar (same row) */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={2}
        flexWrap="wrap"
        minHeight={32}
      >
        <Tooltip
          title={
            isConnected
              ? "Real-time updates enabled"
              : "Connecting to real-time updates..."
          }
        >
          <Chip
            icon={<BoltIcon />}
            label={isConnected ? "Live" : "Offline"}
            color={isConnected ? "success" : "default"}
            variant={isConnected ? "filled" : "outlined"}
          />
        </Tooltip>
        {/* Connection status alert */}
        {!isConnected && (
          <Alert severity="info">
            Real-time updates are offline. Using polling mode. Click Refresh to
            manually update.
          </Alert>
        )}

        {/* Status chips */}
        {ALL_STATUSES.map((s) => {
          const count = rows.filter((r) => r.status === s).length;
          if (count === 0) return null;
          return (
            <Chip
              key={s}
              label={`${s}: ${count}`}
              size="small"
              color={STATUS_COLORS[s] || "default"}
              variant={filterStatus === s ? "filled" : "outlined"}
              onClick={() => setFilterStatus(filterStatus === s ? "" : s)}
              sx={{ cursor: "pointer" }}
            />
          );
        })}
        <FormControlLabel
          control={
            <Switch
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              color="error"
            />
          }
          label="Show Deleted"
        />

        {showDeleted && (
          <Chip
            label={`deleted: ${rows.length}`}
            size="small"
            color="error"
            variant="filled"
          />
        )}

        {/* Select all / Deselect all — always available for the currently filtered rows */}
        {rows.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() =>
              setSelectionModel(
                selectionModel.length === rows.length
                  ? []
                  : rows.map((r) => r.id)
              )
            }
          >
            {selectionModel.length === rows.length
              ? "Deselect all"
              : "Select all"}
          </Button>
        )}

        {/* Bulk actions — shown inline when rows are selected */}
        {selectionModel.length > 0 && (
          <>
            <Box
              sx={{
                mx: 0.5,
                borderLeft: "1px solid",
                borderColor: "divider",
                height: 24,
              }}
            />
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ whiteSpace: "nowrap" }}
            >
              {selectionModel.length} selected:
            </Typography>
            <ButtonGroup size="small" variant="contained" disableElevation>
              <Button
                color="secondary"
                startIcon={<RetryIcon />}
                onClick={handleBulkRetry}
              >
                Retry
              </Button>
              <Button
                color="warning"
                startIcon={<PauseIcon />}
                onClick={handleBulkCancel}
              >
                Cancel
              </Button>
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete
              </Button>
            </ButtonGroup>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => setSelectionModel([])}
            >
              Deselect all
            </Button>
          </>
        )}
      </Box>

      {/* Mobile: Card list */}
      {isMobile ? (
        <Box>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          {/* Initial loading (no data yet) */}
          {isLoading && rows.length === 0 && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && rows.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              No jobs found
            </Typography>
          )}
          {rows.length > 0 && (
            <Stack spacing={1}>
              {rows.map((row) => {
                  const isDeleted = row.audit?.validflag === false;
                  const selected = selectionModel.includes(row.id);
                  const attempts = row.attempts
                    ? `${row.attempts.current}/${row.attempts.max}`
                    : "-";
                  const queuedAt = row.schedule?.queuedAt || row.createdAt;
                  return (
                    <Card
                      key={row.id}
                      variant="outlined"
                      sx={{
                        opacity: isDeleted ? 0.55 : 1,
                        borderColor: selected ? "secondary.main" : "divider",
                        borderWidth: selected ? 2 : 1,
                        bgcolor: isDeleted
                          ? `${theme.palette.error.dark}15`
                          : "background.paper",
                      }}
                    >
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        {/* Header row: checkbox + #num + type + status */}
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          mb={0.5}
                        >
                          <Checkbox
                            size="small"
                            checked={selected}
                            onChange={() =>
                              setSelectionModel((prev) =>
                                prev.includes(row.id)
                                  ? prev.filter((id) => id !== row.id)
                                  : [...prev, row.id],
                              )
                            }
                            sx={{ p: 0.5 }}
                          />
                          <Typography variant="body2" fontWeight={700}>
                            #{row.jobNumber ?? "-"}
                          </Typography>
                          <Chip
                            label={
                              MESSAGE_TYPE_LABELS[row.messageType] ||
                              row.messageType
                            }
                            size="small"
                            variant="outlined"
                          />
                          {row.totalParts > 1 && (
                            <Chip
                              label={`${row.partIndex}/${row.totalParts}`}
                              size="small"
                              color="info"
                              sx={{ fontSize: "0.65rem", height: 18 }}
                            />
                          )}
                          <Box flex={1} />
                          <Chip
                            label={row.status}
                            size="small"
                            color={STATUS_COLORS[row.status] || "default"}
                          />
                        </Box>

                        {/* Phone + attempts + queued */}
                        <Box display="flex" gap={2} flexWrap="wrap" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            📞 {row.destination?.phoneNumber || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ↻ {attempts}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {queuedAt
                              ? moment(queuedAt).format("DD/MM HH:mm:ss")
                              : "-"}
                          </Typography>
                        </Box>

                        {/* Message */}
                        {(() => {
                          const text = row.content?.text || "-";
                          const expanded = expandedIds.has(row.id);
                          const isLong = text.length > 150;
                          const displayed =
                            isLong && !expanded ? `${text.slice(0, 150)}...` : text;
                          return (
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: expanded ? "pre-wrap" : "normal",
                                  wordBreak: "break-word",
                                  bgcolor: "background.default",
                                  p: 1,
                                  borderRadius: 1,
                                  fontSize: "0.8rem",
                                }}
                              >
                                {displayed}
                              </Typography>
                              {isLong && (
                                <Button
                                  size="small"
                                  color="secondary"
                                  variant="text"
                                  onClick={() => toggleExpanded(row.id)}
                                  sx={{ mt: 0.25, p: 0, minWidth: 0, fontSize: "0.7rem" }}
                                >
                                  {expanded ? "Show less" : "Show more"}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Actions */}
                        <Divider sx={{ mb: 0.5 }} />
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetail(row)}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                          {isDeleted ? (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleRestore(row)}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <>
                              {row.status === "failed" && (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleRetry(row)}
                                >
                                  <RetryIcon fontSize="small" />
                                </IconButton>
                              )}
                              {row.status === "cancelled" && (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleResend(row)}
                                >
                                  <ResendIcon fontSize="small" />
                                </IconButton>
                              )}
                              {[
                                "pending",
                                "scheduled",
                                "retry_scheduled",
                              ].includes(row.status) && (
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleCancel(row)}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(row)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
            </Stack>
          )}
          {/* Mobile loading / end indicator */}
          {isLoading && rows.length > 0 && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} color="secondary" />
            </Box>
          )}
          {!mobileHasMore && rows.length > 0 && (
            <Typography variant="caption" color="text.secondary" textAlign="center" display="block" py={2}>
              — End ({rows.length} items) —
            </Typography>
          )}
        </Box>
      ) : (
        <Box height="65vh">
          <StyledDataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            getRowClassName={(params) =>
              params.row.audit?.validflag === false ? "row-deleted" : ""
            }
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            rowsPerPageOptions={[25, 50, 100]}
            checkboxSelection
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelection) =>
              setSelectionModel(newSelection)
            }
            components={{ Toolbar: DataGridCustomToolbar }}
            componentsProps={{
              toolbar: {
                searchInput,
                setSearchInput,
                setSearch,
              },
            }}
            disableSelectionOnClick={false}
            containerSx={{ height: "100%" }}
            autoHeight={false}
            sx={{
              "& .row-deleted": {
                opacity: 0.5,
                textDecoration: "line-through",
                backgroundColor: `${theme.palette.error.dark}22 !important`,
              },
            }}
          />
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Job Details
          {detailJob && (
            <Chip
              label={detailJob.status}
              size="small"
              color={STATUS_COLORS[detailJob.status] || "default"}
              sx={{ ml: 1 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailJob && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Job ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {detailJob.id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2">
                  {MESSAGE_TYPE_LABELS[detailJob.messageType] ||
                    detailJob.messageType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body2">
                  {detailJob.customerName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Phone Number
                </Typography>
                <Typography variant="body2">
                  {detailJob.destination?.phoneNumber || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Source Scene
                </Typography>
                <Typography variant="body2">
                  {detailJob.source?.scene || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Queued At
                </Typography>
                <Typography variant="body2">
                  {detailJob.schedule?.queuedAt
                    ? moment(detailJob.schedule.queuedAt).format(
                        "DD/MM/YYYY HH:mm:ss",
                      )
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Next Attempt At
                </Typography>
                <Typography variant="body2">
                  {detailJob.schedule?.nextAttemptAt
                    ? moment(detailJob.schedule.nextAttemptAt).format(
                        "DD/MM/YYYY HH:mm:ss",
                      )
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Attempts
                </Typography>
                <Typography variant="body2">
                  {detailJob.attempts
                    ? `${detailJob.attempts.current} / ${detailJob.attempts.max}`
                    : "-"}
                </Typography>
              </Grid>
              {detailJob.totalParts > 1 && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Message Part
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Chip
                      label={`Part ${detailJob.partIndex} of ${detailJob.totalParts}`}
                      size="small"
                      color="info"
                    />
                    {detailJob.partIndex > 1 && (
                      <Typography variant="caption" color="text.secondary">
                        Retry this job to resend the missing part only
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  WhatsApp Group
                </Typography>
                <Typography variant="body2">
                  {groupName(detailJob.provider?.selectedGroupId)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Message
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 200,
                    overflowY: "auto",
                    fontSize: "0.875rem",
                  }}
                >
                  {detailJob.content?.text || "-"}
                </Box>
              </Grid>
              {detailJob.relatedPackages?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Related Packages
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                    {detailJob.relatedPackages.map((pkg, i) => (
                      <Chip
                        key={i}
                        label={pkg.tracking}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}
              {detailJob.attempts?.history?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Attempt History
                  </Typography>
                  {detailJob.attempts.history.map((h, i) => (
                    <Box
                      key={i}
                      sx={{
                        mt: 0.5,
                        p: 1,
                        bgcolor: "background.default",
                        borderRadius: 1,
                        fontSize: "0.8rem",
                      }}
                    >
                      <Typography variant="body2">
                        <strong>#{h.attemptNumber}</strong> — {h.outcome}{" "}
                        {h.startedAt &&
                          `at ${moment(h.startedAt).format("DD/MM HH:mm:ss")}`}
                      </Typography>
                      {h.errorMessage && (
                        <Typography variant="body2" color="error">
                          {h.errorMessage}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {detailJob?.status === "failed" && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RetryIcon />}
              onClick={() => {
                setDetailOpen(false);
                handleRetry(detailJob);
              }}
            >
              Retry
            </Button>
          )}
          {detailJob &&
            ["pending", "scheduled", "retry_scheduled"].includes(
              detailJob.status,
            ) && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setDetailOpen(false);
                  handleCancel(detailJob);
                }}
              >
                Cancel Job
              </Button>
            )}
          <Button onClick={() => setDetailOpen(false)} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppQueue;
